import { Observable } from '@legendapp/state'
import { For, Memo, reactive, useObservable, useSelector } from '@legendapp/state/react'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { isEmpty } from 'radash'
import { FC, PropsWithChildren, ReactNode, useEffect } from 'react'

import { MenubarItem, MenubarSub, MenubarSubContent, MenubarSubTrigger } from '~in-app/components'
import { CollapseMenuItem } from '~in-app/components/layout/console/sidebar/CollapseMenuItem'
import { MenuItem } from '~in-app/components/layout/console/sidebar/MenuItem'
import { cn, formatUrlPathname } from '~in-app/lib/utils'
import { MenuItemProps } from '~in-app/routerList'

interface MenuRenderProps {
  isSection?: boolean
  hasParent?: boolean
  name?: string
  label: ReactNode | string
  icon?: ReactNode | string
  path?: string
  items?: MenuItemProps[]
}

const ReactiveMenubarSub = reactive(MenubarSub)

const MenuRecursiveRender: FC<MenuRenderProps> = ({ isSection, icon, items, label, path, hasParent, name }) => {
  const hasItems = Array.isArray(items)

  if (isSection) {
    return (items || []).map((props, index) => (
      <MenuRecursiveRender
        key={`MenuSection_${label}_${path}_${index}`}
        icon={props.icon}
        items={props.children}
        label={props.label}
        name={props.name}
        path={formatUrlPathname(path + (props.path || ''))}
      />
    ))
  }

  if (hasItems && items?.length > 0) {
    return (
      <CollapseMenuItem
        icon={icon}
        items={items}
        label={label}
        name={name!}
        path={formatUrlPathname(path || '')}
      />
    )
  }

  return (
    <MenuItem
      hasParent={hasParent}
      icon={icon}
      label={label}
      path={formatUrlPathname(path || '')}
    />
  )
}

const MenubarSubCustom: FC<PropsWithChildren<{ path: string }>> = ({ children, path }) => {
  const location = useLocation()
  const state$ = useObservable({ isActive: false })

  useEffect(() => {
    const cleanedPath = path || ''
    const currentPath = location.pathname || ''
    const hasActiveChildren = !isEmpty(cleanedPath) && currentPath.startsWith(cleanedPath)

    state$.isActive.set(hasActiveChildren)
  }, [path, location])

  return (
    <ReactiveMenubarSub
      $open={state$.isActive}
      onOpenChange={state$.isActive.set}
    >
      {children}
    </ReactiveMenubarSub>
  )
}

const MenubarItemRender: FC<{ item$: Observable<MenuItemProps>, id?: string, path?: string }> = ({ item$, path: parentPath }) => {
  const subMenus = useSelector(() => item$.children.get())
  const path = item$.path.get()
  const hasPath = !isEmpty(!item$.path.get())
  const exactPath = formatUrlPathname(parentPath + (path || ''))
  const navigate = useNavigate()
  if (isEmpty(subMenus)) {
    return (
      <Link
        disabled={!hasPath}
        to={exactPath}
      >
        {
          ({ isActive }: { isActive: boolean }) => (
            <MenubarItem
              className={
                cn('capitalize text-muted-foreground', {
                  'font-medium dark:text-primary-400 text-primary-600 dark:focus:text-primary-400 focus:text-primary-600 bg-muted': isActive,
                })
              }
              onClick={() => navigate({ to: exactPath })}
            >
              <Memo>{item$.label}</Memo>
            </MenubarItem>
          )
        }

      </Link>
    )
  }

  return (
    <MenubarSubCustom path={exactPath}>
      <MenubarSubTrigger className={cn('capitalize text-muted-foreground')}>
        <Memo>{item$.label}</Memo>
      </MenubarSubTrigger>

      <MenubarSubContent>
        <MenubarRecursiveRender
          items={subMenus}
          path={exactPath}
        />
      </MenubarSubContent>
    </MenubarSubCustom>
  )
}

const MenubarRecursiveRender: FC<Pick<MenuRenderProps, 'items' | 'path'>> = ({ items, path }) => {
  const state$ = useObservable({ items: items || [] })

  return (
    <For
      optimized
      each={state$.items}
      item={MenubarItemRender}
      itemProps={{ path }}
    />
  )
}

export { MenuRecursiveRender, MenubarRecursiveRender }
